/* eslint-disable @typescript-eslint/no-unsafe-argument */
import React, { useContext, useEffect, useMemo, useState } from "react";

import { css } from "@emotion/core";
import { useTheme } from "@emotion/react";
import styled from "@emotion/styled";

import { DataContext } from "../../../../providers/VacancyDataProvider";
import Columns from "../../../shared/Columns";
import NewMultiSelect from "../../../shared/forms/NewMultiSelect";
import Modal from "../../../shared/JobList/Modal";

const ModalHeadline = styled.div`
    display: none;
    padding: 0 15px;
    width: 100%;
    ${({ theme }) => theme.breakpoints.from.medium.css`
       position: sticky;
       top: 60px;
       z-index: 10;
       z-index: 10;
       display: block;
       background-color: white;
    `}
`;

const ModalContent = styled.div`
    padding: 20px 15px;
    width: 100%;
    ${({ theme }) => theme.breakpoints.upTo.medium.css`
       margin-bottom: 30px;
    `}
`;

const Headline = styled.div`
    width: 100%;
    margin-bottom: 5px;
    font-size: 1.5em;
    font-weight: bold;
    color: ${({ theme }) => theme.colors.blue.toString()};
    border-bottom: 2px solid ${({ theme }) => theme.colors.blue.toString()};
`;

const VacancyFilter = () => {
    const theme = useTheme();
    const dataContext = useContext(DataContext);

    const {
        vacancyCategories,
        companies,
        vacancyAudiences,
        cities,
        categoryFilter,
        audienceFilter,
        companyFilter,
        cityFilter,
        isActiveModal,
    } = dataContext.data;
    const {
        setCategoryFilter,
        setAudienceFilter,
        setCompanyFilter,
        setCityFilter,
        setCount,
        setIsActiveModal,
    } = dataContext.actions;

    const [selectedCompanies, setSelectedCompanies] = useState(companyFilter);
    const [selectedCategories, setSelectedCategories] =
        useState(categoryFilter);
    const [selectedAudiences, setSelectedAudiences] = useState(audienceFilter);
    const [selectedCities, setSelectedCities] = useState(cityFilter);

    const handleModal = (event) => {
        if ((event && event.target.classList.contains("backdrop")) || !event) {
            setIsActiveModal(!isActiveModal);
        }
    };

    const onFilter = () => {
        const url = new URL(window.location);
        selectedCompanies
            ? url.searchParams.set("companies", selectedCompanies.toString())
            : url.searchParams.delete("companies");

        selectedCategories
            ? url.searchParams.set("categories", selectedCategories.toString())
            : url.searchParams.delete("categories");

        selectedAudiences
            ? url.searchParams.set("audiences", selectedAudiences.toString())
            : url.searchParams.delete("audiences");

        selectedCities
            ? url.searchParams.set("cities", selectedCities)
            : url.searchParams.delete("cities");

        url.searchParams.set("count", "4");
        window.history.pushState(null, "", url.toString());
        handleModal();
        setCount(4);
        setCompanyFilter(selectedCompanies ? selectedCompanies : []);
        setCategoryFilter(selectedCategories ? selectedCategories : []);
        setAudienceFilter(selectedAudiences ? selectedAudiences : []);
        setCityFilter(selectedCities ? selectedCities : []);
    };

    const handleReset = () => {
        const url = new URL(window.location);
        url.searchParams.delete("companies");
        url.searchParams.delete("categories");
        url.searchParams.delete("audiences");
        url.searchParams.delete("cities");
        url.searchParams.set("count", "4");
        window.history.pushState(null, "", url.toString());
        setSelectedCompanies([]);
        setSelectedCategories([]);
        setSelectedAudiences([]);
        setSelectedCities([]);
        setCompanyFilter([]);
        setCategoryFilter([]);
        setAudienceFilter([]);
        setCityFilter([]);
        setCount(4);
        handleModal();
    };

    //Always use IDs as String because when they come from the URL they are always a string too.
    const companiesMemo = useMemo(() => {
        return companies.map((company) => ({
            value: company.id.toString(),
            text: company.name,
        }));
    }, [companies]);

    const categoriesMemo = useMemo(() => {
        return vacancyCategories.map((category) => ({
            value: category.id.toString(),
            text: category.name,
        }));
    }, [vacancyCategories]);

    const audiencesMemo = useMemo(() => {
        return vacancyAudiences.map((audience) => ({
            value: audience.id.toString(),
            text: audience.name,
        }));
    }, [vacancyAudiences]);

    const citesMemo = useMemo(() => {
        return cities.map((city, index) => ({
            value: index.toString(),
            text: city,
        }));
    }, [cities]);

    useEffect(() => {
        return setSelectedCompanies(
            companyFilter.map((item) => item.toString())
        );
    }, [companyFilter]);

    useEffect(() => {
        return setSelectedCategories(categoryFilter);
    }, [categoryFilter]);

    useEffect(() => {
        return setSelectedAudiences(audienceFilter);
    }, [audienceFilter]);

    useEffect(() => {
        return setSelectedCities(cityFilter);
    }, [cityFilter]);

    return (
        <>
            <Modal
                isActive={isActiveModal}
                handleClose={handleModal}
                handleReset={handleReset}
                handleSubmit={onFilter}
            >
                <ModalHeadline>
                    <Columns>
                        <Columns.Column tablet="full" desktop="one-quarter">
                            <Headline>Unternehmen</Headline>
                        </Columns.Column>
                        <Columns.Column tablet="full" desktop="one-quarter">
                            <Headline>Berufsbezeichnung</Headline>
                        </Columns.Column>
                        <Columns.Column tablet="full" desktop="one-quarter">
                            <Headline>Zielgruppe</Headline>
                        </Columns.Column>
                        <Columns.Column tablet="full" desktop="one-quarter">
                            <Headline>Standorte</Headline>
                        </Columns.Column>
                    </Columns>
                </ModalHeadline>
                <ModalContent>
                    <Columns>
                        <Columns.Column tablet="full" desktop="one-quarter">
                            <Headline
                                css={css`
                                    ${theme.breakpoints.from.medium
                                        .css`display: none;`}

                                    display: block;
                                `}
                            >
                                Unternehmen
                            </Headline>
                            {companiesMemo && (
                                <NewMultiSelect
                                    id="filter-companies"
                                    selected={selectedCompanies}
                                    items={companiesMemo}
                                    itemText="text"
                                    itemValue="value"
                                    onChange={(selected) =>
                                        setSelectedCompanies(selected)
                                    }
                                />
                            )}
                        </Columns.Column>
                        <Columns.Column tablet="full" desktop="one-quarter">
                            <Headline
                                css={css`
                                    ${theme.breakpoints.from.medium
                                        .css`display: none;`}

                                    display: block;
                                `}
                            >
                                Berufsbezeichnung
                            </Headline>
                            {categoriesMemo && (
                                <NewMultiSelect
                                    id="filter-categories"
                                    selected={selectedCategories}
                                    items={categoriesMemo}
                                    itemText="text"
                                    itemValue="value"
                                    onChange={(selected) =>
                                        setSelectedCategories(selected)
                                    }
                                />
                            )}
                        </Columns.Column>
                        <Columns.Column tablet="full" desktop="one-quarter">
                            <Headline
                                css={css`
                                    ${theme.breakpoints.from.medium
                                        .css`display: none;`}

                                    display: block;
                                `}
                            >
                                Zielgruppe
                            </Headline>
                            {audiencesMemo && (
                                <NewMultiSelect
                                    id="filter-audiences"
                                    selected={selectedAudiences}
                                    items={audiencesMemo}
                                    itemText="text"
                                    itemValue="value"
                                    onChange={(selected) =>
                                        setSelectedAudiences(selected)
                                    }
                                />
                            )}
                        </Columns.Column>
                        <Columns.Column tablet="full" desktop="one-quarter">
                            <Headline
                                css={css`
                                    ${theme.breakpoints.from.medium
                                        .css`display: none;`}

                                    display: block;
                                `}
                            >
                                Standorte
                            </Headline>
                            {citesMemo && (
                                <NewMultiSelect
                                    id="filter-cities"
                                    selected={selectedCities}
                                    items={citesMemo}
                                    itemText="text"
                                    itemValue="text"
                                    onChange={(selected) => {
                                        setSelectedCities(selected);
                                    }}
                                />
                            )}
                        </Columns.Column>
                    </Columns>
                </ModalContent>
            </Modal>
        </>
    );
};

export default VacancyFilter;
